import { CityLanguage } from '@/context/languages/languageData';

export interface CitySpecialty {
  id: string;
  slugs: {
    en: string;
    es: string;
    local?: string;
  };
  names: {
    en: string;
    es: string;
    local?: string;
  };
  keywords: {
    en: string[];
    es: string[];
    local?: string[];
  };
  icon: string;
  priority: number;
  description: {
    en: string;
    es: string;
    local?: string;
  };
  longDescription: {
    en: string;
    es: string;
    local?: string;
  };
}

export const citySpecialties: CitySpecialty[] = [
  {
    id: 'couples-therapy',
    slugs: {
      en: 'couples-therapy',
      es: 'psicologo-de-pareja'
    },
    names: {
      en: 'Couples Therapist',
      es: 'Psicólogo de Pareja'
    },
    keywords: {
      en: ['couples therapy', 'marriage counseling', 'relationship therapy', 'couples counselor'],
      es: ['psicólogo de pareja', 'terapia de pareja', 'consejero matrimonial', 'terapeuta de pareja']
    },
    icon: 'Heart',
    priority: 1,
    description: {
      en: 'Professional couples therapy and marriage counseling services',
      es: 'Servicios profesionales de terapia de pareja y asesoramiento matrimonial'
    },
    longDescription: {
      en: 'Specialized couples therapy services using evidence-based approaches like Gottman Method and Emotionally Focused Therapy to strengthen relationships, improve communication, and resolve conflicts between partners.',
      es: 'Servicios especializados de terapia de pareja utilizando enfoques basados en evidencia como el Método Gottman y la Terapia Enfocada en Emociones para fortalecer relaciones, mejorar la comunicación y resolver conflictos entre parejas.'
    }
  },
  {
    id: 'sexologist',
    slugs: {
      en: 'sexologist',
      es: 'sexologo'
    },
    names: {
      en: 'Sexologist',
      es: 'Sexólogo'
    },
    keywords: {
      en: ['sexologist', 'sex therapy', 'sexual health', 'intimacy counseling'],
      es: ['sexólogo', 'terapia sexual', 'salud sexual', 'asesoramiento de intimidad']
    },
    icon: 'UserHeart',
    priority: 2,
    description: {
      en: 'Professional sexology and sexual health therapy services',
      es: 'Servicios profesionales de sexología y terapia de salud sexual'
    },
    longDescription: {
      en: 'Expert sexology services addressing sexual dysfunction, intimacy issues, sexual identity, and relationship satisfaction through evidence-based therapeutic approaches.',
      es: 'Servicios expertos de sexología que abordan la disfunción sexual, problemas de intimidad, identidad sexual y satisfacción en la relación mediante enfoques terapéuticos basados en evidencia.'
    }
  },
  {
    id: 'child-psychologist',
    slugs: {
      en: 'child-psychologist',
      es: 'psicologo-infantil'
    },
    names: {
      en: 'Child Psychologist',
      es: 'Psicólogo Infantil'
    },
    keywords: {
      en: ['child psychologist', 'child therapy', 'pediatric psychology', 'play therapy'],
      es: ['psicólogo infantil', 'terapia infantil', 'psicología pediátrica', 'terapia de juego']
    },
    icon: 'Baby',
    priority: 3,
    description: {
      en: 'Specialized child psychology and pediatric mental health services',
      es: 'Servicios especializados de psicología infantil y salud mental pediátrica'
    },
    longDescription: {
      en: 'Child psychology services including play therapy, behavioral interventions, and developmental support for children facing emotional, behavioral, and developmental challenges.',
      es: 'Servicios de psicología infantil incluyendo terapia de juego, intervenciones conductuales y apoyo del desarrollo para niños que enfrentan desafíos emocionales, conductuales y del desarrollo.'
    }
  },
  {
    id: 'adolescent-psychologist',
    slugs: {
      en: 'adolescent-psychologist',
      es: 'psicologo-para-adolescentes'
    },
    names: {
      en: 'Adolescent Psychologist',
      es: 'Psicólogo para Adolescentes'
    },
    keywords: {
      en: ['adolescent psychologist', 'teen therapy', 'youth counseling', 'teenage mental health'],
      es: ['psicólogo para adolescentes', 'terapia para adolescentes', 'consejería juvenil', 'salud mental adolescente']
    },
    icon: 'Users',
    priority: 4,
    description: {
      en: 'Professional adolescent psychology and teen mental health services',
      es: 'Servicios profesionales de psicología para adolescentes y salud mental juvenil'
    },
    longDescription: {
      en: 'Adolescent psychology services addressing identity issues, peer relationships, academic stress, family conflicts, and mental health challenges specific to teenage years.',
      es: 'Servicios de psicología para adolescentes que abordan problemas de identidad, relaciones entre pares, estrés académico, conflictos familiares y desafíos de salud mental específicos de la adolescencia.'
    }
  },
  {
    id: 'child-neuropsychologist',
    slugs: {
      en: 'child-neuropsychologist',
      es: 'neuropsicologo-infantil'
    },
    names: {
      en: 'Child Neuropsychologist',
      es: 'Neuropsicólogo Infantil'
    },
    keywords: {
      en: ['child neuropsychologist', 'pediatric neuropsychology', 'cognitive assessment', 'learning disabilities'],
      es: ['neuropsicólogo infantil', 'neuropsicología pediátrica', 'evaluación cognitiva', 'dificultades de aprendizaje']
    },
    icon: 'Brain',
    priority: 5,
    description: {
      en: 'Specialized child neuropsychology and cognitive assessment services',
      es: 'Servicios especializados de neuropsicología infantil y evaluación cognitiva'
    },
    longDescription: {
      en: 'Child neuropsychology services including cognitive assessments, learning disability evaluations, and interventions for neurodevelopmental disorders and brain-related conditions.',
      es: 'Servicios de neuropsicología infantil incluyendo evaluaciones cognitivas, evaluaciones de dificultades de aprendizaje e intervenciones para trastornos del neurodesarrollo y condiciones relacionadas con el cerebro.'
    }
  },
  {
    id: 'adult-neuropsychologist',
    slugs: {
      en: 'adult-neuropsychologist',
      es: 'neuropsicologo-adultos'
    },
    names: {
      en: 'Adult Neuropsychologist',
      es: 'Neuropsicólogo de Adultos'
    },
    keywords: {
      en: ['adult neuropsychologist', 'cognitive rehabilitation', 'memory assessment', 'brain injury therapy'],
      es: ['neuropsicólogo de adultos', 'rehabilitación cognitiva', 'evaluación de memoria', 'terapia de lesión cerebral']
    },
    icon: 'Brain',
    priority: 6,
    description: {
      en: 'Professional adult neuropsychology and cognitive rehabilitation services',
      es: 'Servicios profesionales de neuropsicología de adultos y rehabilitación cognitiva'
    },
    longDescription: {
      en: 'Adult neuropsychology services including cognitive assessments, memory rehabilitation, and therapeutic interventions for brain injuries, dementia, and neurological conditions.',
      es: 'Servicios de neuropsicología de adultos incluyendo evaluaciones cognitivas, rehabilitación de memoria e intervenciones terapéuticas para lesiones cerebrales, demencia y condiciones neurológicas.'
    }
  },
  {
    id: 'addiction-psychologist',
    slugs: {
      en: 'addiction-psychologist',
      es: 'psicologo-de-adicciones'
    },
    names: {
      en: 'Addiction Psychologist',
      es: 'Psicólogo de Adicciones'
    },
    keywords: {
      en: ['addiction psychologist', 'substance abuse therapy', 'addiction recovery', 'addiction counseling'],
      es: ['psicólogo de adicciones', 'terapia de abuso de sustancias', 'recuperación de adicciones', 'consejería de adicciones']
    },
    icon: 'HeartPulse',
    priority: 7,
    description: {
      en: 'Professional addiction psychology and recovery support services',
      es: 'Servicios profesionales de psicología de adicciones y apoyo para la recuperación'
    },
    longDescription: {
      en: 'Addiction psychology services including individual counseling, group therapy, relapse prevention, and comprehensive support for substance abuse and behavioral addiction recovery.',
      es: 'Servicios de psicología de adicciones incluyendo consejería individual, terapia grupal, prevención de recaídas y apoyo integral para la recuperación del abuso de sustancias y adicciones conductuales.'
    }
  },
  {
    id: 'smoking-cessation',
    slugs: {
      en: 'smoking-cessation-psychologist',
      es: 'psicologo-para-dejar-de-fumar'
    },
    names: {
      en: 'Smoking Cessation Psychologist',
      es: 'Psicólogo para Dejar de Fumar'
    },
    keywords: {
      en: ['smoking cessation', 'quit smoking therapy', 'nicotine addiction', 'tobacco cessation'],
      es: ['dejar de fumar', 'terapia para dejar de fumar', 'adicción a la nicotina', 'cesación del tabaco']
    },
    icon: 'Ban',
    priority: 8,
    description: {
      en: 'Specialized smoking cessation therapy and nicotine addiction treatment',
      es: 'Terapia especializada para dejar de fumar y tratamiento de adicción a la nicotina'
    },
    longDescription: {
      en: 'Smoking cessation services using cognitive-behavioral therapy, motivational interviewing, and evidence-based strategies to help individuals quit smoking and overcome nicotine addiction.',
      es: 'Servicios para dejar de fumar utilizando terapia cognitivo-conductual, entrevista motivacional y estrategias basadas en evidencia para ayudar a las personas a dejar de fumar y superar la adicción a la nicotina.'
    }
  },
  {
    id: 'alcoholism-psychologist',
    slugs: {
      en: 'alcoholism-psychologist',
      es: 'psicologo-para-alcoholismo'
    },
    names: {
      en: 'Alcoholism Psychologist',
      es: 'Psicólogo para Alcoholismo'
    },
    keywords: {
      en: ['alcoholism psychologist', 'alcohol addiction therapy', 'alcohol recovery', 'alcohol abuse counseling'],
      es: ['psicólogo para alcoholismo', 'terapia de adicción al alcohol', 'recuperación del alcohol', 'consejería de abuso de alcohol']
    },
    icon: 'Wine',
    priority: 9,
    description: {
      en: 'Professional alcoholism therapy and alcohol addiction recovery services',
      es: 'Servicios profesionales de terapia para alcoholismo y recuperación de adicción al alcohol'
    },
    longDescription: {
      en: 'Alcoholism psychology services including comprehensive assessment, individual and group therapy, family support, and evidence-based interventions for alcohol use disorder and recovery.',
      es: 'Servicios de psicología para alcoholismo incluyendo evaluación integral, terapia individual y grupal, apoyo familiar e intervenciones basadas en evidencia para el trastorno por consumo de alcohol y recuperación.'
    }
  },
  {
    id: 'occupational-psychologist',
    slugs: {
      en: 'occupational-psychologist',
      es: 'psicologo-laboral'
    },
    names: {
      en: 'Occupational Psychologist',
      es: 'Psicólogo Laboral'
    },
    keywords: {
      en: ['occupational psychologist', 'work psychology', 'workplace stress', 'career counseling'],
      es: ['psicólogo laboral', 'psicología del trabajo', 'estrés laboral', 'asesoramiento profesional']
    },
    icon: 'Briefcase',
    priority: 10,
    description: {
      en: 'Professional occupational psychology and workplace mental health services',
      es: 'Servicios profesionales de psicología laboral y salud mental en el trabajo'
    },
    longDescription: {
      en: 'Occupational psychology services addressing workplace stress, burnout, career transitions, work-life balance, and organizational mental health through evidence-based interventions.',
      es: 'Servicios de psicología laboral que abordan el estrés laboral, agotamiento, transiciones profesionales, equilibrio vida-trabajo y salud mental organizacional mediante intervenciones basadas en evidencia.'
    }
  },
  {
    id: 'stress-psychologist',
    slugs: {
      en: 'stress-psychologist',
      es: 'psicologo-para-estres'
    },
    names: {
      en: 'Stress Management Psychologist',
      es: 'Psicólogo para Estrés'
    },
    keywords: {
      en: ['stress psychologist', 'stress management', 'burnout therapy', 'stress reduction'],
      es: ['psicólogo para estrés', 'manejo del estrés', 'terapia para burnout', 'reducción del estrés']
    },
    icon: 'Activity',
    priority: 11,
    description: {
      en: 'Professional stress management and burnout prevention services',
      es: 'Servicios profesionales de manejo del estrés y prevención del agotamiento'
    },
    longDescription: {
      en: 'Stress management services including relaxation techniques, mindfulness training, cognitive-behavioral strategies, and coping skills for chronic stress, acute stress, and burnout prevention.',
      es: 'Servicios de manejo del estrés incluyendo técnicas de relajación, entrenamiento en mindfulness, estrategias cognitivo-conductuales y habilidades de afrontamiento para estrés crónico, estrés agudo y prevención del agotamiento.'
    }
  },
  {
    id: 'geriatric-psychologist',
    slugs: {
      en: 'geriatric-psychologist',
      es: 'psicologo-para-personas-mayores'
    },
    names: {
      en: 'Geriatric Psychologist',
      es: 'Psicólogo para Personas Mayores'
    },
    keywords: {
      en: ['geriatric psychologist', 'elderly mental health', 'aging psychology', 'senior care'],
      es: ['psicólogo para personas mayores', 'salud mental de ancianos', 'psicología del envejecimiento', 'cuidado de mayores']
    },
    icon: 'Users',
    priority: 12,
    description: {
      en: 'Specialized geriatric psychology and elderly mental health services',
      es: 'Servicios especializados de psicología geriátrica y salud mental para personas mayores'
    },
    longDescription: {
      en: 'Geriatric psychology services addressing age-related cognitive decline, depression in older adults, grief and loss, chronic illness adaptation, and quality of life enhancement for seniors.',
      es: 'Servicios de psicología geriátrica que abordan el deterioro cognitivo relacionado con la edad, depresión en adultos mayores, duelo y pérdida, adaptación a enfermedades crónicas y mejora de la calidad de vida para personas mayores.'
    }
  },
  {
    id: 'anxiety-psychologist',
    slugs: {
      en: 'anxiety-psychologist',
      es: 'psicologo-para-ansiedad'
    },
    names: {
      en: 'Anxiety Psychologist',
      es: 'Psicólogo para Ansiedad'
    },
    keywords: {
      en: ['anxiety psychologist', 'anxiety therapy', 'panic disorder', 'social anxiety'],
      es: ['psicólogo para ansiedad', 'terapia de ansiedad', 'trastorno de pánico', 'ansiedad social']
    },
    icon: 'AlertCircle',
    priority: 13,
    description: {
      en: 'Professional anxiety therapy and panic disorder treatment services',
      es: 'Servicios profesionales de terapia para ansiedad y tratamiento de trastorno de pánico'
    },
    longDescription: {
      en: 'Anxiety psychology services including cognitive-behavioral therapy, exposure therapy, and mindfulness-based approaches for generalized anxiety, panic disorder, social anxiety, and phobias.',
      es: 'Servicios de psicología para ansiedad incluyendo terapia cognitivo-conductual, terapia de exposición y enfoques basados en mindfulness para ansiedad generalizada, trastorno de pánico, ansiedad social y fobias.'
    }
  },
  {
    id: 'depression-psychologist',
    slugs: {
      en: 'depression-psychologist',
      es: 'psicologo-para-depresion'
    },
    names: {
      en: 'Depression Psychologist',
      es: 'Psicólogo para Depresión'
    },
    keywords: {
      en: ['depression psychologist', 'depression therapy', 'mood disorder', 'clinical depression'],
      es: ['psicólogo para depresión', 'terapia para depresión', 'trastorno del estado de ánimo', 'depresión clínica']
    },
    icon: 'CloudRain',
    priority: 14,
    description: {
      en: 'Professional depression therapy and mood disorder treatment services',
      es: 'Servicios profesionales de terapia para depresión y tratamiento de trastornos del estado de ánimo'
    },
    longDescription: {
      en: 'Depression psychology services including evidence-based psychotherapy, cognitive-behavioral therapy, and supportive counseling for major depression, persistent depressive disorder, and seasonal affective disorder.',
      es: 'Servicios de psicología para depresión incluyendo psicoterapia basada en evidencia, terapia cognitivo-conductual y consejería de apoyo para depresión mayor, trastorno depresivo persistente y trastorno afectivo estacional.'
    }
  },
  {
    id: 'trauma-psychologist',
    slugs: {
      en: 'trauma-psychologist',
      es: 'psicologo-para-trauma'
    },
    names: {
      en: 'Trauma Psychologist',
      es: 'Psicólogo para Trauma'
    },
    keywords: {
      en: ['trauma psychologist', 'PTSD therapy', 'trauma recovery', 'EMDR therapy'],
      es: ['psicólogo para trauma', 'terapia TEPT', 'recuperación de trauma', 'terapia EMDR']
    },
    icon: 'Shield',
    priority: 15,
    description: {
      en: 'Specialized trauma therapy and PTSD treatment services',
      es: 'Servicios especializados de terapia para trauma y tratamiento de TEPT'
    },
    longDescription: {
      en: 'Trauma psychology services including EMDR, cognitive processing therapy, and prolonged exposure therapy for PTSD, complex trauma, childhood trauma, and traumatic stress recovery.',
      es: 'Servicios de psicología para trauma incluyendo EMDR, terapia de procesamiento cognitivo y terapia de exposición prolongada para TEPT, trauma complejo, trauma infantil y recuperación de estrés traumático.'
    }
  },
  {
    id: 'adhd-psychologist',
    slugs: {
      en: 'adhd-psychologist',
      es: 'psicologo-para-tdah'
    },
    names: {
      en: 'ADHD Psychologist',
      es: 'Psicólogo para TDAH'
    },
    keywords: {
      en: ['ADHD psychologist', 'attention deficit therapy', 'ADHD treatment', 'executive function'],
      es: ['psicólogo para TDAH', 'terapia de déficit de atención', 'tratamiento TDAH', 'función ejecutiva']
    },
    icon: 'Zap',
    priority: 16,
    description: {
      en: 'Professional ADHD therapy and attention deficit treatment services',
      es: 'Servicios profesionales de terapia para TDAH y tratamiento de déficit de atención'
    },
    longDescription: {
      en: 'ADHD psychology services including behavioral interventions, executive function training, coping strategies, and comprehensive support for children and adults with attention deficit hyperactivity disorder.',
      es: 'Servicios de psicología para TDAH incluyendo intervenciones conductuales, entrenamiento de función ejecutiva, estrategias de afrontamiento y apoyo integral para niños y adultos con trastorno por déficit de atención e hiperactividad.'
    }
  },
  {
    id: 'child-behavior-psychologist',
    slugs: {
      en: 'child-behavior-psychologist',
      es: 'psicologo-para-problemas-de-conducta-infantil'
    },
    names: {
      en: 'Child Behavior Psychologist',
      es: 'Psicólogo para Problemas de Conducta Infantil'
    },
    keywords: {
      en: ['child behavior psychologist', 'behavioral problems', 'oppositional defiant disorder', 'conduct disorder'],
      es: ['psicólogo de conducta infantil', 'problemas de comportamiento', 'trastorno oposicionista desafiante', 'trastorno de conducta']
    },
    icon: 'AlertTriangle',
    priority: 17,
    description: {
      en: 'Specialized child behavior therapy and conduct disorder treatment',
      es: 'Terapia especializada de conducta infantil y tratamiento de trastornos de conducta'
    },
    longDescription: {
      en: 'Child behavior psychology services including behavioral interventions, parent training, and therapeutic strategies for oppositional defiant disorder, conduct problems, and behavioral challenges in children.',
      es: 'Servicios de psicología de conducta infantil incluyendo intervenciones conductuales, entrenamiento para padres y estrategias terapéuticas para trastorno oposicionista desafiante, problemas de conducta y desafíos conductuales en niños.'
    }
  },
  {
    id: 'bullying-psychologist',
    slugs: {
      en: 'bullying-psychologist',
      es: 'psicologo-para-bullying'
    },
    names: {
      en: 'Bullying Psychologist',
      es: 'Psicólogo para Bullying'
    },
    keywords: {
      en: ['bullying psychologist', 'bullying therapy', 'cyberbullying', 'school bullying'],
      es: ['psicólogo para bullying', 'terapia para acoso escolar', 'ciberacoso', 'acoso escolar']
    },
    icon: 'ShieldAlert',
    priority: 18,
    description: {
      en: 'Professional bullying intervention and cyberbullying therapy services',
      es: 'Servicios profesionales de intervención en bullying y terapia para ciberacoso'
    },
    longDescription: {
      en: 'Bullying psychology services including trauma-informed therapy for bullying victims, intervention strategies, family support, and evidence-based approaches for school bullying and cyberbullying recovery.',
      es: 'Servicios de psicología para bullying incluyendo terapia informada en trauma para víctimas de acoso, estrategias de intervención, apoyo familiar y enfoques basados en evidencia para recuperación de acoso escolar y ciberacoso.'
    }
  },
  {
    id: 'emotional-dependency-psychologist',
    slugs: {
      en: 'emotional-dependency-psychologist',
      es: 'psicologo-para-dependencia-emocional'
    },
    names: {
      en: 'Emotional Dependency Psychologist',
      es: 'Psicólogo para Dependencia Emocional'
    },
    keywords: {
      en: ['emotional dependency psychologist', 'codependency therapy', 'relationship addiction', 'attachment issues'],
      es: ['psicólogo para dependencia emocional', 'terapia de codependencia', 'adicción a las relaciones', 'problemas de apego']
    },
    icon: 'Link',
    priority: 19,
    description: {
      en: 'Professional emotional dependency and codependency therapy services',
      es: 'Servicios profesionales de terapia para dependencia emocional y codependencia'
    },
    longDescription: {
      en: 'Emotional dependency psychology services addressing codependency patterns, attachment issues, relationship addiction, and helping individuals develop healthy independence and self-esteem.',
      es: 'Servicios de psicología para dependencia emocional que abordan patrones de codependencia, problemas de apego, adicción a las relaciones y ayudan a las personas a desarrollar independencia saludable y autoestima.'
    }
  },
  {
    id: 'online-therapy',
    slugs: {
      en: 'online-therapy',
      es: 'psicologo-online'
    },
    names: {
      en: 'Online Psychologist',
      es: 'Psicólogo Online'
    },
    keywords: {
      en: ['online psychologist', 'teletherapy', 'virtual therapy', 'online counseling'],
      es: ['psicólogo online', 'teleterapia', 'terapia virtual', 'consejería online']
    },
    icon: 'Video',
    priority: 20,
    description: {
      en: 'Professional online therapy and telepsychology services',
      es: 'Servicios profesionales de terapia online y telepsicología'
    },
    longDescription: {
      en: 'Online psychology services providing convenient, accessible, and effective virtual therapy through secure video conferencing for a wide range of mental health concerns from anywhere.',
      es: 'Servicios de psicología online que proporcionan terapia virtual conveniente, accesible y efectiva mediante videoconferencia segura para una amplia gama de preocupaciones de salud mental desde cualquier lugar.'
    }
  },
  {
    id: 'psychiatrist',
    slugs: {
      en: 'psychiatrist',
      es: 'psiquiatra'
    },
    names: {
      en: 'Psychiatrist',
      es: 'Psiquiatra'
    },
    keywords: {
      en: ['psychiatrist', 'psychiatric care', 'medication management', 'mental health diagnosis'],
      es: ['psiquiatra', 'atención psiquiátrica', 'gestión de medicamentos', 'diagnóstico de salud mental']
    },
    icon: 'Stethoscope',
    priority: 21,
    description: {
      en: 'Professional psychiatric care and medication management services',
      es: 'Servicios profesionales de atención psiquiátrica y gestión de medicamentos'
    },
    longDescription: {
      en: 'Psychiatry services including comprehensive mental health diagnosis, medication management, psychiatric treatment planning, and integrated care for complex mental health conditions.',
      es: 'Servicios de psiquiatría incluyendo diagnóstico integral de salud mental, gestión de medicamentos, planificación de tratamiento psiquiátrico y atención integrada para condiciones de salud mental complejas.'
    }
  },
  {
    id: 'psychotherapist',
    slugs: {
      en: 'psychotherapist',
      es: 'psicoterapeuta'
    },
    names: {
      en: 'Psychotherapist',
      es: 'Psicoterapeuta'
    },
    keywords: {
      en: ['psychotherapist', 'psychotherapy', 'talk therapy', 'therapeutic relationship'],
      es: ['psicoterapeuta', 'psicoterapia', 'terapia de conversación', 'relación terapéutica']
    },
    icon: 'MessageCircle',
    priority: 22,
    description: {
      en: 'Professional psychotherapy and therapeutic counseling services',
      es: 'Servicios profesionales de psicoterapia y consejería terapéutica'
    },
    longDescription: {
      en: 'Psychotherapy services using evidence-based therapeutic approaches including cognitive-behavioral therapy, psychodynamic therapy, and integrative methods for mental health and personal growth.',
      es: 'Servicios de psicoterapia utilizando enfoques terapéuticos basados en evidencia incluyendo terapia cognitivo-conductual, terapia psicodinámica y métodos integrativos para la salud mental y el crecimiento personal.'
    }
  },
  {
    id: 'child-psychiatrist',
    slugs: {
      en: 'child-psychiatrist',
      es: 'psiquiatra-infantil'
    },
    names: {
      en: 'Child Psychiatrist',
      es: 'Psiquiatra Infantil'
    },
    keywords: {
      en: ['child psychiatrist', 'pediatric psychiatry', 'child medication management', 'developmental disorders'],
      es: ['psiquiatra infantil', 'psiquiatría pediátrica', 'gestión de medicamentos infantiles', 'trastornos del desarrollo']
    },
    icon: 'HeartPulse',
    priority: 23,
    description: {
      en: 'Specialized child psychiatry and pediatric mental health services',
      es: 'Servicios especializados de psiquiatría infantil y salud mental pediátrica'
    },
    longDescription: {
      en: 'Child psychiatry services including comprehensive evaluation, medication management, and integrated treatment for neurodevelopmental disorders, childhood mental health conditions, and behavioral challenges.',
      es: 'Servicios de psiquiatría infantil incluyendo evaluación integral, gestión de medicamentos y tratamiento integrado para trastornos del neurodesarrollo, condiciones de salud mental infantil y desafíos conductuales.'
    }
  },
  {
    id: 'therapist',
    slugs: {
      en: 'therapist',
      es: 'terapeuta'
    },
    names: {
      en: 'Therapist',
      es: 'Terapeuta'
    },
    keywords: {
      en: ['therapist', 'therapy sessions', 'mental wellness', 'counseling'],
      es: ['terapeuta', 'sesiones de terapia', 'bienestar mental', 'asesoramiento']
    },
    icon: 'Users2',
    priority: 24,
    description: {
      en: 'Professional therapy and mental wellness counseling services',
      es: 'Servicios profesionales de terapia y asesoramiento de bienestar mental'
    },
    longDescription: {
      en: 'General therapy services providing compassionate support, evidence-based interventions, and personalized treatment for a wide range of mental health concerns and life challenges.',
      es: 'Servicios generales de terapia proporcionando apoyo compasivo, intervenciones basadas en evidencia y tratamiento personalizado para una amplia gama de preocupaciones de salud mental y desafíos de la vida.'
    }
  },
  {
    id: 'educational-psychologist',
    slugs: {
      en: 'educational-psychologist',
      es: 'psicopedagogo'
    },
    names: {
      en: 'Educational Psychologist',
      es: 'Psicopedagogo'
    },
    keywords: {
      en: ['educational psychologist', 'learning difficulties', 'educational assessment', 'academic support'],
      es: ['psicopedagogo', 'dificultades de aprendizaje', 'evaluación educativa', 'apoyo académico']
    },
    icon: 'GraduationCap',
    priority: 25,
    description: {
      en: 'Specialized educational psychology and learning support services',
      es: 'Servicios especializados de psicopedagogía y apoyo al aprendizaje'
    },
    longDescription: {
      en: 'Educational psychology services including learning disability assessments, academic interventions, study skills training, and comprehensive support for students facing educational challenges.',
      es: 'Servicios de psicopedagogía incluyendo evaluaciones de dificultades de aprendizaje, intervenciones académicas, entrenamiento en técnicas de estudio y apoyo integral para estudiantes que enfrentan desafíos educativos.'
    }
  },
  {
    id: 'speech-therapist',
    slugs: {
      en: 'speech-therapist',
      es: 'logopeda'
    },
    names: {
      en: 'Speech Therapist',
      es: 'Logopeda'
    },
    keywords: {
      en: ['speech therapist', 'speech therapy', 'language disorders', 'communication problems'],
      es: ['logopeda', 'terapia del habla', 'trastornos del lenguaje', 'problemas de comunicación']
    },
    icon: 'Mic',
    priority: 26,
    description: {
      en: 'Professional speech therapy and communication disorder services',
      es: 'Servicios profesionales de logopedia y trastornos de la comunicación'
    },
    longDescription: {
      en: 'Speech therapy services addressing articulation disorders, language delays, fluency problems, voice disorders, and comprehensive communication support for all ages.',
      es: 'Servicios de logopedia que abordan trastornos de articulación, retrasos en el lenguaje, problemas de fluidez, trastornos de la voz y apoyo integral de comunicación para todas las edades.'
    }
  }
];

export const getCitySpecialtyById = (id: string): CitySpecialty | undefined => {
  return citySpecialties.find(s => s.id === id);
};

export const getCitySpecialtyBySlug = (slug: string, language: CityLanguage = 'en'): CitySpecialty | undefined => {
  return citySpecialties.find(s => 
    s.slugs[language] === slug || s.slugs.en === slug || s.slugs.es === slug
  );
};
